// Deadline-bucket logic. Buckets are exclusive — every task lands in exactly one.
//   completed   → status === "done"
//   overdue     → has deadline & deadline < startOfDay(now) & not done
//   today       → deadline within today
//   this_week   → deadline within current ISO week (Mon..Sun)
//   next_week   → deadline within next ISO week
//   later       → deadline after next-week-end
//   no_deadline → no deadline & not done

export const BUCKET_KEYS = ["overdue", "today", "this_week", "next_week", "later", "no_deadline", "completed"];

export const BUCKET_META = {
  overdue:     { label: "Overdue",       color: "#EF4444", subtle: "#FEE2E2", help: "Past deadline" },
  today:       { label: "Due Today",     color: "#F59E0B", subtle: "#FEF3C7", help: "Today" },
  this_week:   { label: "Due This Week", color: "#3B82F6", subtle: "#DBEAFE", help: "By end of this week" },
  next_week:   { label: "Due Next Week", color: "#6366F1", subtle: "#E0E7FF", help: "Next week" },
  later:       { label: "Due Later",     color: "#94A3B8", subtle: "#F1F5F9", help: "Beyond next week" },
  no_deadline: { label: "No Deadline",   color: "#64748B", subtle: "#F1F5F9", help: "Unscheduled" },
  completed:   { label: "Completed",     color: "#10B981", subtle: "#D1FAE5", help: "Done" },
};

function startOfDay(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
}
function endOfDay(d) {
  const x = new Date(d); x.setHours(23, 59, 59, 999); return x;
}
function endOfWeek(d) {
  // ISO week ends Sunday (getDay 0). Use Sunday as end of "this week" matching English calendar weeks.
  const x = endOfDay(d);
  const day = x.getDay(); // 0=Sun, 1=Mon, ...
  const daysUntilSunday = (7 - day) % 7;
  x.setDate(x.getDate() + daysUntilSunday);
  return endOfDay(x);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function bucketFor(task, now = new Date()) {
  if (task.status === "done") return "completed";
  if (!task.dueDate) return "no_deadline";
  const d = new Date(task.dueDate);
  if (isNaN(d.getTime())) return "no_deadline";
  const sod = startOfDay(now);
  if (d < sod) return "overdue";
  if (d <= endOfDay(now)) return "today";
  const thisWeekEnd = endOfWeek(now);
  if (d <= thisWeekEnd) return "this_week";
  const nextWeekEnd = endOfWeek(addDays(thisWeekEnd, 1));
  if (d <= nextWeekEnd) return "next_week";
  return "later";
}

// When a card is dropped on a column, pick a sensible deadline.
// Returns either:
//   { dueDate: ISOString }      — set the deadline
//   { dueDate: null }           — clear deadline
//   { status: "done" }          — mark complete
//   null                        — invalid drop (no-op)
// Existing time-of-day on the task is preserved when feasible.
export function moveForBucket(bucketKey, task, now = new Date()) {
  if (bucketKey === "completed") return { status: "done" };
  if (bucketKey === "no_deadline") return { dueDate: null };
  if (bucketKey === "overdue") return null; // can't drop into overdue
  const baseTime = task.dueDate ? new Date(task.dueDate) : null;
  function preserveTime(target) {
    if (baseTime && !isNaN(baseTime.getTime())) {
      target.setHours(baseTime.getHours(), baseTime.getMinutes(), 0, 0);
    } else {
      target.setHours(17, 0, 0, 0); // default 5 PM
    }
    return target;
  }
  let target;
  if (bucketKey === "today") target = preserveTime(new Date(now));
  else if (bucketKey === "this_week") target = preserveTime(endOfWeek(now));
  else if (bucketKey === "next_week") target = preserveTime(endOfWeek(addDays(endOfWeek(now), 1)));
  else if (bucketKey === "later") {
    const t = endOfWeek(addDays(endOfWeek(now), 1));
    target = preserveTime(addDays(t, 14));
  } else return null;

  // If movement would be backwards (target < startOfToday) and we picked "today", clamp to today.
  // This is normal — no special handling needed.
  // Drop status="done" if currently done; we want to revive when moving into a deadline column.
  const result = { dueDate: target.toISOString() };
  if (task.status === "done") result.status = "todo";
  return result;
}

// Format helper for cards
export function formatDeadlineBadge(d) {
  if (!d) return null;
  const dt = new Date(d);
  const now = new Date();
  const sameYear = dt.getFullYear() === now.getFullYear();
  const opts = sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" };
  const dateStr = dt.toLocaleDateString(undefined, opts);
  const timeStr = dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${dateStr}, ${timeStr}`;
}
